import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";

import {
  Section,
  Wrapper,
  renderSectionTitle,
} from "../../../common/FormUtils";
import ActionFormButton from "../../../common/ActionFormButton";
import {
  Distribution,
  DistributionStatus,
} from "../../../../types/Distribution";
import {
  uploadAppleSecret,
  uploadGoogleSecret,
  uploadAppleIapSecret,
  uploadApnsSecret,
  validateApple,
  validateGoogle,
} from "../../../../database/distribution/distributionApi";

type Props = {
  item: Distribution | null;
  communityId?: string;
  updatedBy: string;
  mode: string;
  isActive?: boolean;
  onChageFormToEditMode: () => void;
  onSaveMetadata: (input: Partial<Distribution> & { id: string; updatedBy: string }) => Promise<void>;
};

type Platform = "apple" | "google";

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3) var(--space-4);
  margin-bottom: var(--space-4);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
`;

const FieldLabel = styled.label`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
`;

const FieldHint = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  line-height: 1.35;

  a {
    color: var(--color-accent);
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const TextInput = styled.input`
  height: 40px;
  padding: 0 var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);

  &:focus {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const SecretPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
  margin-bottom: var(--space-4);
`;

const SecretHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
`;

const SecretMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SecretTitle = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const SecretHint = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);

  a {
    color: var(--color-accent);
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const StatusPill = styled.span<{ $status: DistributionStatus }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 2px var(--space-2);
  border-radius: 999px;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);

  ${(p) =>
    p.$status === "valid" &&
    css`
      background: rgba(34, 197, 94, 0.12);
      color: rgb(21, 128, 61);
    `}
  ${(p) =>
    p.$status === "invalid" &&
    css`
      background: rgba(239, 68, 68, 0.12);
      color: rgb(185, 28, 28);
    `}
  ${(p) =>
    p.$status === "pending" &&
    css`
      background: rgba(234, 179, 8, 0.12);
      color: rgb(161, 98, 7);
    `}
  ${(p) =>
    p.$status === "unset" &&
    css`
      background: var(--color-surface);
      color: var(--color-text-secondary);
      border: 1px solid var(--color-border);
    `}
`;

const StatusDot = styled.span<{ $status: DistributionStatus }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) =>
    p.$status === "valid"
      ? "rgb(34, 197, 94)"
      : p.$status === "invalid"
      ? "rgb(239, 68, 68)"
      : p.$status === "pending"
      ? "rgb(234, 179, 8)"
      : "var(--color-text-disabled, #999)"};
`;

const ButtonRow = styled.div`
  display: inline-flex;
  gap: var(--space-2);
  flex-wrap: wrap;
`;

const SecretButton = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  height: 32px;
  padding: 0 var(--space-3);
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-standard);

  ${(p) =>
    p.$primary
      ? css`
          background: var(--color-accent);
          color: var(--color-on-accent);
          border: 1px solid var(--color-accent);
          &:hover:not(:disabled) {
            background: var(--color-accent-strong, var(--color-accent));
          }
        `
      : css`
          background: var(--color-surface);
          color: var(--color-text);
          border: 1px solid var(--color-border);
          &:hover:not(:disabled) {
            background: var(--color-surface-subtle);
          }
        `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: rgb(185, 28, 28);
`;

const HiddenFileInput = styled.input.attrs({ type: "file" })`
  display: none;
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  cursor: pointer;
  margin-bottom: var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
`;

const statusLabel = (status: DistributionStatus): string => {
  switch (status) {
    case "valid":
      return "Valid";
    case "invalid":
      return "Invalid";
    case "pending":
      return "Checking";
    default:
      return "Not uploaded";
  }
};

const formatWhen = (epochMs?: number): string => {
  if (!epochMs) return "never";
  const now = Date.now();
  const diff = Math.max(0, now - epochMs);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const DistributionForm = ({
  item,
  communityId,
  updatedBy,
  mode,
  isActive,
  onChageFormToEditMode,
  onSaveMetadata,
}: Props) => {
  const [teamId, setTeamId] = useState("");
  const [bundleId, setBundleId] = useState("");
  const [ascIssuerId, setAscIssuerId] = useState("");
  const [ascKeyId, setAscKeyId] = useState("");
  const [ascAppId, setAscAppId] = useState("");
  const [packageName, setPackageName] = useState("");
  const [easProjectId, setEasProjectId] = useState("");
  const [iapKeyId, setIapKeyId] = useState("");
  const [showIapKey, setShowIapKey] = useState(false);
  const [apnsKeyId, setApnsKeyId] = useState("");
  const [showApnsKey, setShowApnsKey] = useState(false);

  const [uploadingApple, setUploadingApple] = useState(false);
  const [uploadingGoogle, setUploadingGoogle] = useState(false);
  const [uploadingIap, setUploadingIap] = useState(false);
  const [uploadingApns, setUploadingApns] = useState(false);
  const [validatingApple, setValidatingApple] = useState(false);
  const [validatingGoogle, setValidatingGoogle] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTeamId(item?.apple?.teamId ?? "");
    setBundleId(item?.apple?.bundleId ?? "");
    setAscIssuerId(item?.apple?.ascIssuerId ?? "");
    setAscKeyId(item?.apple?.ascKeyId ?? "");
    setAscAppId(item?.apple?.ascAppId ?? "");
    setPackageName(item?.google?.packageName ?? "");
    setEasProjectId(item?.easProjectId ?? "");
    setIapKeyId(item?.apple?.iapKeyId ?? "");
    setShowIapKey(
      Boolean(item?.apple?.iapKeyId || item?.apple?.iapSecretRef)
    );
    setApnsKeyId(item?.apple?.apnsKeyId ?? "");
    setShowApnsKey(
      Boolean(item?.apple?.apnsKeyId || item?.apple?.apnsSecretRef)
    );
  }, [item]);

  const appleStatus = item?.apple?.status ?? "unset";
  const googleStatus = item?.google?.status ?? "unset";
  const appleSecretUploaded = Boolean(item?.apple?.ascSecretRef);
  const googleSecretUploaded = Boolean(item?.google?.saSecretRef);
  const iapSecretUploaded = Boolean(item?.apple?.iapSecretRef);
  const apnsSecretUploaded = Boolean(item?.apple?.apnsSecretRef);

  const handleFilePicked = async (
    platform: Platform,
    file: File | null
  ): Promise<void> => {
    setError(null);
    if (!file) {
      setError("No file was selected.");
      return;
    }
    if (!communityId) {
      setError(
        "No community is selected, so the key can't be uploaded. Reload the page or reselect the community."
      );
      return;
    }
    try {
      if (platform === "apple") {
        setUploadingApple(true);
        await uploadAppleSecret(communityId, file);
      } else {
        setUploadingGoogle(true);
        await uploadGoogleSecret(communityId, file);
      }
    } catch (e: any) {
      // Surface the underlying Firebase error so failures are diagnosable
      // (e.g. function not deployed, Secret Manager not enabled, permissions).
      // eslint-disable-next-line no-console
      console.error("Distribution upload failed", {
        platform,
        code: e?.code,
        message: e?.message,
        details: e?.details,
        error: e,
      });
      const code = e?.code ? ` (${e.code})` : "";
      setError((e?.message ?? `Failed to upload ${platform} secret`) + code);
    } finally {
      setUploadingApple(false);
      setUploadingGoogle(false);
    }
  };

  // Apple In-App Purchase key (.p8) for the App Store Server API. Uploaded to
  // Secret Manager just like the ASC key; only a reference is stored.
  const handleIapFilePicked = async (file: File | null): Promise<void> => {
    setError(null);
    if (!file) {
      setError("No file was selected.");
      return;
    }
    if (!communityId) {
      setError(
        "No community is selected, so the key can't be uploaded. Reload the page or reselect the community."
      );
      return;
    }
    try {
      setUploadingIap(true);
      await uploadAppleIapSecret(communityId, file);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error("In-app purchase key upload failed", {
        code: e?.code,
        message: e?.message,
        details: e?.details,
        error: e,
      });
      const code = e?.code ? ` (${e.code})` : "";
      setError((e?.message ?? "Failed to upload in-app purchase key") + code);
    } finally {
      setUploadingIap(false);
    }
  };

  // APNs auth key (.p8) for push notifications. Same Secret Manager flow as
  // the IAP key; stored under apple.apns* (shared with the MCP tool).
  const handleApnsFilePicked = async (file: File | null): Promise<void> => {
    setError(null);
    if (!file) {
      setError("No file was selected.");
      return;
    }
    if (!communityId) {
      setError(
        "No community is selected, so the key can't be uploaded. Reload the page or reselect the community."
      );
      return;
    }
    try {
      setUploadingApns(true);
      await uploadApnsSecret(communityId, file);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error("APNs key upload failed", {
        code: e?.code,
        message: e?.message,
        details: e?.details,
        error: e,
      });
      const code = e?.code ? ` (${e.code})` : "";
      setError((e?.message ?? "Failed to upload push notifications key") + code);
    } finally {
      setUploadingApns(false);
    }
  };

  // Persist the current form fields to Firestore. Validation runs server-side
  // and reads the issuer/key/package values from the stored doc, so they must
  // be saved before "Validate now" can succeed.
  const persistMetadata = async (): Promise<void> => {
    if (!communityId) return;
    await onSaveMetadata({
      id: communityId,
      updatedBy,
      apple: {
        ...(item?.apple ?? { status: "unset" as DistributionStatus }),
        teamId,
        bundleId,
        ascIssuerId,
        ascKeyId,
        ascAppId,
        iapKeyId,
        apnsKeyId,
      },
      google: {
        ...(item?.google ?? { status: "unset" as DistributionStatus }),
        packageName,
      },
      easProjectId,
    });
  };

  const handleValidate = async (platform: Platform): Promise<void> => {
    if (!communityId) return;
    setError(null);
    try {
      if (platform === "apple") {
        setValidatingApple(true);
        // Save current metadata first so the server validates the latest values.
        await persistMetadata();
        const r = await validateApple(communityId);
        if (!r.ok) setError(r.error ?? "Apple validation failed");
      } else {
        setValidatingGoogle(true);
        await persistMetadata();
        const r = await validateGoogle(communityId);
        if (!r.ok) setError(r.error ?? "Google validation failed");
      }
    } catch (e: any) {
      setError(e?.message ?? `Failed to validate ${platform} credentials`);
    } finally {
      setValidatingApple(false);
      setValidatingGoogle(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!communityId) return;
    setError(null);
    setSubmitting(true);
    try {
      await persistMetadata();
    } catch (e: any) {
      setError(e?.message ?? "Failed to save distribution settings");
    } finally {
      setSubmitting(false);
    }
  };

  const renderSecretPanel = (
    platform: Platform,
    title: string,
    hint: React.ReactNode,
    accept: string,
    uploaded: boolean,
    status: DistributionStatus,
    lastValidatedAt: number | undefined,
    isUploading: boolean,
    isValidating: boolean
  ) => {
    const inputId = `distribution-${platform}-file`;
    return (
      <SecretPanel>
        <SecretHeader>
          <SecretMeta>
            <SecretTitle>{title}</SecretTitle>
            <SecretHint>
              {uploaded
                ? `Last validated ${formatWhen(lastValidatedAt)}`
                : hint}
            </SecretHint>
          </SecretMeta>
          <StatusPill $status={status}>
            <StatusDot $status={status} />
            {statusLabel(status)}
          </StatusPill>
        </SecretHeader>

        <ButtonRow>
          <HiddenFileInput
            id={inputId}
            accept={accept}
            onChange={(e) =>
              handleFilePicked(platform, e.target.files?.[0] ?? null)
            }
          />
          <SecretButton
            as="label"
            htmlFor={inputId}
            $primary={!uploaded}
            aria-disabled={isUploading}
          >
            {isUploading
              ? "Uploading..."
              : uploaded
              ? "Replace key"
              : "Upload key"}
          </SecretButton>
          <SecretButton
            type="button"
            disabled={!uploaded || isValidating}
            onClick={() => handleValidate(platform)}
          >
            {isValidating ? "Validating..." : "Validate now"}
          </SecretButton>
        </ButtonRow>
      </SecretPanel>
    );
  };

  return (
    <Wrapper>
      <Section>
        {renderSectionTitle("Apple App Store")}
        <FieldGrid>
          <FieldGroup>
            <FieldLabel htmlFor="apple-team-id">Team ID</FieldLabel>
            <TextInput
              id="apple-team-id"
              type="text"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              disabled={mode === "detail"}
              placeholder="e.g. ABCDE12345"
            />
            <FieldHint>
              10-character ID at{" "}
              <a
                href="https://developer.apple.com/account"
                target="_blank"
                rel="noreferrer"
              >
                developer.apple.com/account
              </a>{" "}
              → Membership details card → Team ID.
            </FieldHint>
          </FieldGroup>
          <FieldGroup>
            <FieldLabel htmlFor="apple-bundle-id">Bundle ID</FieldLabel>
            <TextInput
              id="apple-bundle-id"
              type="text"
              value={bundleId}
              onChange={(e) => setBundleId(e.target.value)}
              disabled={mode === "detail"}
              placeholder="e.g. com.brand.app"
            />
            <FieldHint>
              Reverse-domain app identifier. See{" "}
              <a
                href="https://developer.apple.com/account/resources/identifiers/list"
                target="_blank"
                rel="noreferrer"
              >
                registered identifiers
              </a>{" "}
              or App Store Connect → App Information → Bundle ID.
            </FieldHint>
          </FieldGroup>
          <FieldGroup>
            <FieldLabel htmlFor="apple-issuer-id">
              App Store Connect Issuer ID
            </FieldLabel>
            <TextInput
              id="apple-issuer-id"
              type="text"
              value={ascIssuerId}
              onChange={(e) => setAscIssuerId(e.target.value)}
              disabled={mode === "detail"}
              placeholder="UUID from App Store Connect"
            />
            <FieldHint>
              The "Issuer ID" above the keys table at{" "}
              <a
                href="https://appstoreconnect.apple.com/access/integrations/api"
                target="_blank"
                rel="noreferrer"
              >
                App Store Connect → Integrations → API
              </a>
              .
            </FieldHint>
          </FieldGroup>
          <FieldGroup>
            <FieldLabel htmlFor="apple-key-id">
              App Store Connect Key ID
            </FieldLabel>
            <TextInput
              id="apple-key-id"
              type="text"
              value={ascKeyId}
              onChange={(e) => setAscKeyId(e.target.value)}
              disabled={mode === "detail"}
              placeholder="10-character key id"
            />
            <FieldHint>
              The "Key ID" column next to the key you create at{" "}
              <a
                href="https://appstoreconnect.apple.com/access/integrations/api"
                target="_blank"
                rel="noreferrer"
              >
                Integrations → API
              </a>{" "}
              (matches the .p8 below).
            </FieldHint>
          </FieldGroup>
          <FieldGroup>
            <FieldLabel htmlFor="apple-asc-app-id">App Store app ID</FieldLabel>
            <TextInput
              id="apple-asc-app-id"
              type="text"
              value={ascAppId}
              onChange={(e) => setAscAppId(e.target.value)}
              disabled={mode === "detail"}
              placeholder="numeric app id, e.g. 1234567890"
            />
            <FieldHint>
              The app's numeric Apple ID at App Store Connect → your app → App
              Information → General → "Apple ID". Lets the publish script target
              the exact app on upload.
            </FieldHint>
          </FieldGroup>
        </FieldGrid>

        {renderSecretPanel(
          "apple",
          "App Store Connect API key (.p8)",
          <>
            Generated under Users and Access &gt; Integrations &gt; Keys with
            Admin role (App Manager can submit but cannot generate signing
            certificates).{" "}
            <a
              href="https://tribed.io/guides/appstore-connect-api-key"
              target="_blank"
              rel="noreferrer"
            >
              Step-by-step guide with screenshots
            </a>
            .
          </>,
          ".p8",
          appleSecretUploaded,
          appleStatus,
          item?.apple?.lastValidatedAt,
          uploadingApple,
          validatingApple
        )}

        <ToggleLabel>
          <input
            type="checkbox"
            checked={showIapKey}
            onChange={(e) => setShowIapKey(e.target.checked)}
            disabled={mode === "detail"}
          />
          Add App Store subscription key (In-App Purchase .p8)
        </ToggleLabel>

        {showIapKey && (
          <>
            <FieldGrid>
              <FieldGroup>
                <FieldLabel htmlFor="apple-iap-key-id">
                  In-App Purchase Key ID
                </FieldLabel>
                <TextInput
                  id="apple-iap-key-id"
                  type="text"
                  value={iapKeyId}
                  onChange={(e) => setIapKeyId(e.target.value)}
                  disabled={mode === "detail"}
                  placeholder="10-character key id"
                />
                <FieldHint>
                  The "Key ID" of the In-App Purchase key at{" "}
                  <a
                    href="https://appstoreconnect.apple.com/access/integrations/api"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Integrations → In-App Purchase
                  </a>
                  . Uses the same Issuer ID as above. Needed by RevenueCat /
                  the App Store Server API.
                </FieldHint>
              </FieldGroup>
            </FieldGrid>

            <SecretPanel>
              <SecretHeader>
                <SecretMeta>
                  <SecretTitle>In-App Purchase key (.p8)</SecretTitle>
                  <SecretHint>
                    {iapSecretUploaded ? (
                      `Uploaded ${formatWhen(item?.apple?.iapUploadedAt)}`
                    ) : (
                      <>
                        Generated under Users and Access &gt; Integrations &gt;
                        In-App Purchase. Apple only lets you download it once.{" "}
                        <a
                          href="https://tribed.io/guides/apple-subscriptions-key"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Step-by-step guide with screenshots
                        </a>
                        .
                      </>
                    )}
                  </SecretHint>
                </SecretMeta>
                <StatusPill $status={iapSecretUploaded ? "valid" : "unset"}>
                  <StatusDot $status={iapSecretUploaded ? "valid" : "unset"} />
                  {iapSecretUploaded ? "Uploaded" : "Not uploaded"}
                </StatusPill>
              </SecretHeader>

              <ButtonRow>
                <HiddenFileInput
                  id="distribution-apple-iap-file"
                  accept=".p8"
                  onChange={(e) =>
                    handleIapFilePicked(e.target.files?.[0] ?? null)
                  }
                />
                <SecretButton
                  as="label"
                  htmlFor="distribution-apple-iap-file"
                  $primary={!iapSecretUploaded}
                  aria-disabled={uploadingIap}
                >
                  {uploadingIap
                    ? "Uploading..."
                    : iapSecretUploaded
                    ? "Replace key"
                    : "Upload key"}
                </SecretButton>
              </ButtonRow>
            </SecretPanel>
          </>
        )}

        <ToggleLabel>
          <input
            type="checkbox"
            checked={showApnsKey}
            onChange={(e) => setShowApnsKey(e.target.checked)}
            disabled={mode === "detail"}
          />
          Add push notifications key (APNs .p8)
        </ToggleLabel>

        {showApnsKey && (
          <>
            <FieldGrid>
              <FieldGroup>
                <FieldLabel htmlFor="apple-apns-key-id">
                  APNs Key ID
                </FieldLabel>
                <TextInput
                  id="apple-apns-key-id"
                  type="text"
                  value={apnsKeyId}
                  onChange={(e) => setApnsKeyId(e.target.value)}
                  disabled={mode === "detail"}
                  placeholder="10-character key id"
                />
                <FieldHint>
                  The "Key ID" next to the key at{" "}
                  <a
                    href="https://developer.apple.com/account/resources/authkeys/list"
                    target="_blank"
                    rel="noreferrer"
                  >
                    developer.apple.com → Keys
                  </a>
                  . Uses the Team ID above. One key covers all apps on the
                  team.
                </FieldHint>
              </FieldGroup>
            </FieldGrid>

            <SecretPanel>
              <SecretHeader>
                <SecretMeta>
                  <SecretTitle>Push notifications key (.p8)</SecretTitle>
                  <SecretHint>
                    {apnsSecretUploaded ? (
                      `Uploaded ${formatWhen(item?.apple?.apnsUploadedAt)}`
                    ) : (
                      <>
                        Generated under Certificates, IDs &amp; Profiles &gt;
                        Keys with Apple Push Notifications service enabled.
                        Apple only lets you download it once.{" "}
                        <a
                          href="https://tribed.io/guides/apple-push-key"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Step-by-step guide
                        </a>
                        .
                      </>
                    )}
                  </SecretHint>
                </SecretMeta>
                <StatusPill $status={apnsSecretUploaded ? "valid" : "unset"}>
                  <StatusDot $status={apnsSecretUploaded ? "valid" : "unset"} />
                  {apnsSecretUploaded ? "Uploaded" : "Not uploaded"}
                </StatusPill>
              </SecretHeader>

              <ButtonRow>
                <HiddenFileInput
                  id="distribution-apple-apns-file"
                  accept=".p8"
                  onChange={(e) =>
                    handleApnsFilePicked(e.target.files?.[0] ?? null)
                  }
                />
                <SecretButton
                  as="label"
                  htmlFor="distribution-apple-apns-file"
                  $primary={!apnsSecretUploaded}
                  aria-disabled={uploadingApns}
                >
                  {uploadingApns
                    ? "Uploading..."
                    : apnsSecretUploaded
                    ? "Replace key"
                    : "Upload key"}
                </SecretButton>
              </ButtonRow>
            </SecretPanel>
          </>
        )}
      </Section>

      <Section>
        {renderSectionTitle("Google Play")}
        <FieldGrid>
          <FieldGroup>
            <FieldLabel htmlFor="google-package-name">Package name</FieldLabel>
            <TextInput
              id="google-package-name"
              type="text"
              value={packageName}
              onChange={(e) => setPackageName(e.target.value)}
              disabled={mode === "detail"}
              placeholder="e.g. com.brand.app"
            />
            <FieldHint>
              The applicationId shown under the app name in{" "}
              <a
                href="https://play.google.com/console"
                target="_blank"
                rel="noreferrer"
              >
                Google Play Console
              </a>{" "}
              (e.g. com.brand.app). Can't change after first upload.
            </FieldHint>
          </FieldGroup>
        </FieldGrid>

        {renderSecretPanel(
          "google",
          "Play Developer service account (JSON)",
          <>
            GCP service account JSON, granted Release manager + financial in
            Play Console.{" "}
            <a
              href="https://tribed.io/guides/google-play-credentials"
              target="_blank"
              rel="noreferrer"
            >
              Step-by-step guide with screenshots
            </a>
            .
          </>,
          ".json,application/json",
          googleSecretUploaded,
          googleStatus,
          item?.google?.lastValidatedAt,
          uploadingGoogle,
          validatingGoogle
        )}
      </Section>

      <Section>
        {renderSectionTitle("Build profile")}
        <FieldGrid>
          <FieldGroup>
            <FieldLabel htmlFor="eas-project-id">EAS project ID</FieldLabel>
            <TextInput
              id="eas-project-id"
              type="text"
              value={easProjectId}
              onChange={(e) => setEasProjectId(e.target.value)}
              disabled={mode === "detail"}
              placeholder="Project ID from EAS dashboard"
            />
            <FieldHint>
              <a href="https://expo.dev/accounts" target="_blank" rel="noreferrer">
                expo.dev
              </a>{" "}
              → your project → Project settings → Project ID (UUID), or run{" "}
              <code>eas project:info</code> in the app repo.
            </FieldHint>
          </FieldGroup>
        </FieldGrid>
      </Section>

      {error && <ErrorText>{error}</ErrorText>}

      <ActionFormButton
        onChageFormToEditMode={onChageFormToEditMode}
        onClick={handleSubmit}
        onRemoveItem={() => {}}
        disabled={submitting}
        entity="Distribution"
        mode={mode}
        hidden={!isActive}
      />
    </Wrapper>
  );
};

export default DistributionForm;
